import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnInit } from '@angular/core';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { map, combineLatest, Subscription, distinctUntilChanged } from 'rxjs';
import { Logger, OrganizationService } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { Product, ProductDomain, OfferProductService, OfferService, Offer, OfferDomain } from '../..';

const log = new Logger('OfferAddProductsComponent');
interface DialogData {
  offerId: string;
}

@Component({
  selector: 'app-offer-add-products',
  templateUrl: './offer-add-products.component.html',
  styleUrls: ['./offer-add-products.component.scss']
})

export class OfferAddProductsComponent<T extends Product, TProductDomain extends ProductDomain<Product>, TOffer extends Offer,TOfferDomain extends OfferDomain<Offer>,> implements OnInit {
  viewModel$ = combineLatest([
    this.offerProductService.notOfferProduct$,
    this.offerProductService.notOfferProductisLoading$,
    this.offerProductService.totalProductRecords$,
    this.offerProductService.notOfferProductPage$,
  ]).pipe(
    map(([notOfferproducts, isLoading, totalProductRecords, page]) => {
      return { notOfferproducts, isLoading, totalProductRecords, page }
    }),
  );

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'ProductName', 'ProductCategoryName', 'ProductBrandName', 'Id', 'ClientKey', 'Actions'];
  productsList: ProductDomain<T>[] = [];
  selection = new SelectionModel<ProductDomain<T>>(true, []);
  productId: string = '0';
  offerId: string = '0';

  constructor(private offerProductService: OfferProductService<T>,
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private matSnackBar: MatSnackBar,
    private mediaObserver: MediaObserver,
    public offerService: OfferService<TOffer, TOfferDomain>,
    public dialogRef: MatDialogRef<OfferAddProductsComponent<T, TProductDomain, TOffer, TOfferDomain>>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData) { }

  ngOnInit(): void {
    log.debug('init');
    this.clearSearch();
    this.offerProductService.notOfferProductReload();
    this.offerId = this.route.snapshot.params['offerId'];
    this.productId = this.route.snapshot.params['productId'];
    this.offerProductService.notOfferProduct$.subscribe((data) => {
      if(Array.isArray(data)){
         this.productsList = data;
      }
    })
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'ProductName', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'ProductName', 'ProductCategoryName', 'ProductBrandName', 'Id', 'ClientKey', 'Actions'];
        }
      });

  }
  filtersForm = new FormGroup({
    search: new FormControl(),
  });

  closeDialog() {
    this.dialogRef.close();
  }


  saveData() {
    if (this.selection && this.selection.selected && this.selection.selected.length > 0) {
      let products: any = this.selection.selected.map(x => x);
      const offers = (this.offerService.selection.selected && this.offerService.selection.selected.length > 0) ? this.offerService.selection.selected.map(x => x.Id) : [this.offerId];
      this.offerProductService.associateProductToOffer(offers, products).subscribe({
        next: () => {
          this.matSnackBar.open(`Product(s) are added`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.offerProductService.resetpage();
          this.offerProductService.reload();
          this.dialogRef.close(true);
        },
        error: (error) => {
          log.error('Error adding product to offer', error);

          if (error.error.value) {
            throw new Error(error.error.value);
          } else {
            throw new Error(error.message);
          }
        }
      });
    }
  }

  onSort(sortState: Sort): void {
    this.offerProductService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.offerProductService.notOfferProductPage(pageEvent);
  }

  onSearch(event: any) {
    this.offerProductService.dialogSearch(event.target.value);
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.offerProductService.dialogSearch('');
  }

  onRowDoubleClick(id: string) {
    this.router.navigate([`${id}/details`], { relativeTo: this.route });
  }

  onProductEdit(id: string) {
    this.dialogRef.close();
    this.router.navigate([`${this.organizationService.organization?.name.toLowerCase()}/products`, id, 'details'], { relativeTo: this.route });
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.productsList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.productsList.forEach(row => this.selection.select(row));
  }
}
